/**
 * Helpers para extracao de mensagens de erro do envelope Manager.
 *
 * Contexto (BUG-013 + AUDIT-03, regressivo-pos-migracao 2026-07-29):
 *
 * O backend Node retorna `{codigo, mensagem, detalhes}` (PT). O backend Java
 * legado retorna `{message}` (EN). Durante a migracao ambos coexistiram — e
 * varios `.subscribe({error})` no fed foram escritos apenas com `error.error.message`,
 * silenciando as mensagens PT vindas do Node (o fallback generico sempre batia).
 *
 * Este helper centraliza a cascata correta pra prevenir reincidencia:
 *   1. `error.error.mensagem`  ← envelope Manager (PT), formato canonico atual
 *   2. `error.error.message`   ← envelope legado Java (EN), coexistencia migracao
 *   3. `error.message`         ← mensagem raiz do HttpErrorResponse
 *   4. fallback                ← quando nada esta disponivel
 *
 * Uso:
 * ```typescript
 * error: (err) => {
 *   const detail = extractManagerErrorMessage(err, 'Falha ao criar gestor.');
 *   this.messageService.add({ severity: 'error', summary: 'Erro', detail });
 * }
 * ```
 */

/**
 * Shape parcial do envelope de erro Manager (Node) + fallback Java legado.
 * `unknown` na entrada porque `.subscribe({error})` recebe `any` do RxJS.
 */
interface ManagerErrorLike {
  error?: {
    mensagem?: string | null;
    message?: string | null;
  } | null;
  message?: string | null;
}

/**
 * Extrai mensagem legivel do envelope de erro Manager, com cascata PT → EN → root → fallback.
 *
 * NAO substitui `ErrorToastService.show(apiError, {context})` — esse handler
 * ja usa `apiError.mensagem` diretamente. Use este helper apenas em
 * `messageService.add({severity:'error', detail: ...})` fora do fluxo Toast padrao.
 *
 * @param err  qualquer objeto de erro (HttpErrorResponse, Error, unknown)
 * @param fallback  mensagem exibida quando nenhum campo esta disponivel
 * @returns string sempre truthy (fallback garantido se tudo falhar)
 */
export function extractManagerErrorMessage(err: unknown, fallback: string): string {
  const e = err as ManagerErrorLike | null | undefined;
  return (
    e?.error?.mensagem ||
    e?.error?.message ||
    e?.message ||
    fallback
  );
}

/**
 * Mensagem para exibição INLINE (caixa de erro na própria tela, não toast).
 *
 * Diferente de `extractManagerErrorMessage`: nunca cai no `error.message`/`message`
 * cru. Esses campos também aparecem em respostas que não são do envelope Manager
 * — um 404 de rota inexistente ("Cannot POST /x") ou um 500 sem corpo JSON —, e
 * exibir esse texto de framework na tela é pior que o fallback genérico. Só
 * `error.mensagem` (envelope Node, PT) é confiável o bastante pra ir pro usuário
 * sem revisão; qualquer outra coisa diferente disso é "no máximo algum erro".
 *
 * @returns string sempre truthy (fallback garantido se tudo falhar)
 */
export function extractManagerMensagemInline(err: unknown, fallback: string): string {
  const e = err as ManagerErrorLike | null | undefined;
  return e?.error?.mensagem || fallback;
}

/**
 * Motivo técnico do erro, para log/toast — nunca pra caixa inline (ver acima).
 * Mesma cascata ampla de `extractManagerErrorMessage`, nome diferente só pra
 * deixar explícito no call site que este valor é pra depuração, não pro usuário.
 */
export function extractManagerMotivoTecnico(err: unknown): string {
  const e = err as ManagerErrorLike | null | undefined;
  return e?.error?.mensagem || e?.error?.message || e?.message || 'Erro desconhecido.';
}
